import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getConversions, getConversionStats, updateConversion, refreshConversionEventTime } from '../services/conversionService';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/formatting';
import { FiEdit, FiRefreshCw } from 'react-icons/fi';
import api from '../services/api';
import {
  formatDateToMadridReadable,
  convertUTCToMadridDateTimeLocalString,
  convertMadridDateTimeLocalToUTCString
} from '../utils/dateUtils';
import { parseISO } from 'date-fns';

const ConversionsScreen = () => {
  const [loading, setLoading] = useState(true);
  const [conversions, setConversions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    totalValue: 0,
    averageValue: 0,
    withValueCount: 0,
    withValuePercent: 0
  });
  
  // Estado para el loading específico de la edición
  const [isEditing, setIsEditing] = useState(false);
  const [refreshingRowId, setRefreshingRowId] = useState(null);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  
  // Filtros
  const [filters, setFilters] = useState({
    sortField: 'createdAt',
    sortOrder: 'desc',
    search: '',
    platform: '',
    saleStatus: '',
    dateRange: {
      type: 'all',
      startDate: '',
      endDate: ''
    },
    filterByZeroHour: false
  });

  // Estado para almacenar los filtros temporales antes de aplicarlos
  const [tempFilters, setTempFilters] = useState({
    search: '',
    platform: '',
    saleStatus: '',
    dateRange: {
      type: 'all',
      startDate: '',
      endDate: ''
    },
    filterByZeroHour: false
  });

  // Estado para edición de conversión
  const [selectedConversion, setSelectedConversion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    conversionValue: '',
    conversion_event_time: '',
    gclid: ''
  });

  // Estado para el diálogo de eliminación
  const [deleteDialog, setDeleteDialog] = useState({ 
    open: false, 
    conversion: null 
  });

  // Estado para selección múltiple y marcado para re-exportar
  const [selectedConversions, setSelectedConversions] = useState([]);
  const [showMarkDialog, setShowMarkDialog] = useState(false);
  const [markDuration, setMarkDuration] = useState(48);

  // Calcular los botones de paginación a mostrar
  const paginationButtons = useMemo(() => {
    const buttons = [];
    if (totalPages <= 5) {
      // Si hay 5 o menos páginas, mostrar todas
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      // Si hay más de 5 páginas, decidir qué mostrar
      if (currentPage <= 3) {
        // Si estamos en las primeras páginas
        for (let i = 1; i <= 5; i++) {
          buttons.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        // Si estamos en las últimas páginas
        for (let i = totalPages - 4; i <= totalPages; i++) {
          buttons.push(i);
        }
      } else {
        // Si estamos en páginas intermedias
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          buttons.push(i);
        }
      }
    }
    return buttons;
  }, [currentPage, totalPages]);

  // Efecto para cargar los datos iniciales
  useEffect(() => {
    console.log('Ejecutando efecto - currentPage:', currentPage, 'limit:', limit, 'filters:', filters);
    loadConversions();
  }, [currentPage, limit, filters]);

  // Función para verificar si la hora de una fecha UTC es 00:00:00
  const isZeroUTCHour = (dateString) => {
    if (!dateString) return false;
    try {
      const date = parseISO(dateString); // date-fns parseISO maneja correctamente strings ISO
      return date.getUTCHours() === 0 && 
             date.getUTCMinutes() === 0 && 
             date.getUTCSeconds() === 0;
    } catch (error) {
      console.error("Error al parsear la fecha para isZeroUTCHour:", dateString, error);
      return false;
    }
  };

  // Función para manejar el refresco de la hora del evento
  const handleRefreshTime = async (conversionId) => {
    setRefreshingRowId(conversionId);
    try {
      // LLamada al servicio usando la función importada individualmente
      const response = await refreshConversionEventTime(conversionId);
      
      // El backend devuelve la conversión actualizada directamente si tuvo éxito,
      // o un objeto { message, conversion } si no pudo actualizar desde CEM.
      if (response && response.message) { // No se pudo actualizar desde CEM, pero la llamada fue "exitosa"
        toast.custom((t) => (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'}
            max-w-md w-full bg-base-100 shadow-lg rounded-lg pointer-events-auto flex border border-base-200`}
          >
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-base-content">
                    Información
                  </p>
                  <p className="mt-1 text-sm text-base-content/70">
                    {response.message}
                    {response.conversion && response.conversion.Sale?.id && (
                      <span className="block mt-1">
                        <Link 
                          to={`/sales/${response.conversion.Sale.id}`} 
                          onClick={() => toast.dismiss(t.id)}
                          className="link link-primary text-sm font-medium"
                        >
                          Ir a Venta #{response.conversion.Sale.id} (Conversión #{response.conversion.id})
                        </Link>
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-base-200">
              <button
                onClick={() => toast.dismiss(t.id)}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-primary hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                Cerrar
              </button>
            </div>
          </div>
        ), { duration: 6000 });
        // Actualizar la fila con los datos devueltos (que serán los originales en este caso)
        if (response.conversion) {
             setConversions(prev => prev.map(c => c.id === conversionId ? response.conversion : c));
        }
      } else if (response && response.id) { // Actualización exitosa desde CEM
        setConversions(prev => prev.map(c => c.id === conversionId ? response : c));
        toast.success(
          (t) => (
            <span>
              Hora del evento actualizada para Conversión #{response.id}.
              {response.Sale?.id && (
                <>
                  <br />
                  <Link 
                    to={`/sales/${response.Sale.id}`} 
                    onClick={() => toast.dismiss(t.id)}
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Ver Venta #{response.Sale.id}
                  </Link>
                </>
              )}
            </span>
          ),
          { duration: 6000 }
        );
      } else { // Respuesta inesperada
        console.error("Respuesta inesperada al refrescar hora:", response);
        const affectedConvElse = conversions.find(c => c.id === conversionId);
        const saleIdElse = affectedConvElse?.Sale?.id;
        toast.error(
          (t) => (
            <span>
              Respuesta inesperada del servidor para Conversión #{conversionId}.
              {saleIdElse && (
                <>
                  <br />
                  <Link 
                    to={`/sales/${saleIdElse}`} 
                    onClick={() => toast.dismiss(t.id)}
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Ver Venta #{saleIdElse}
                  </Link>
                </>
              )}
            </span>
          ),
          { duration: 6000 }
        );
      }

    } catch (error) {
      console.error('Error al refrescar la hora del evento:', error);
      const affectedConvCatch = conversions.find(c => c.id === conversionId);
      const saleIdCatch = affectedConvCatch?.Sale?.id;
      const baseErrorMessage = error.response?.data?.message || 'Error al refrescar la hora del evento.';
      
      toast.error(
        (t) => (
          <span>
            {baseErrorMessage} (Conversión #{conversionId})
            {saleIdCatch && (
              <>
                <br />
                <Link 
                  to={`/sales/${saleIdCatch}`} 
                  onClick={() => toast.dismiss(t.id)}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Ver Venta #{saleIdCatch}
                </Link>
              </>
            )}
          </span>
        ),
        { duration: 6000 }
      );
    } finally {
      setRefreshingRowId(null);
    }
  };

  // Cargar estadísticas
  const loadStats = async () => {
    try {
      // Construir parámetros para la API igual que en loadConversions
      const params = {};
      if (filters.platform) params.platform = filters.platform;
      if (filters.saleStatus) params.saleStatus = filters.saleStatus;
      if (filters.search && filters.search.trim() !== '') params.search = filters.search.trim();
      if (filters.filterByZeroHour) params.filterByZeroHour = true;
      if (filters.dateRange.type === 'custom' && filters.dateRange.startDate && filters.dateRange.endDate) {
        params.startDate = filters.dateRange.startDate;
        params.endDate = filters.dateRange.endDate;
      } else if (filters.dateRange.type !== 'all') {
        const dateRange = calculateDateRange(filters.dateRange.type);
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      const data = await getConversionStats(params);
      setStats(data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  // Cargar plataformas activas
  const loadPlatforms = async () => {
    try {
      const response = await api.get('/api/platforms/active');
      setPlatforms(response.data);
    } catch (error) {
      console.error('Error al cargar plataformas:', error);
      toast.error('Error al cargar las plataformas');
    }
  };

  // Cambiar página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Cambiar ordenamiento
  const handleSortChange = (field) => {
    const newSortOrder = 
      filters.sortField === field && filters.sortOrder === 'asc' 
        ? 'desc' 
        : 'asc';
    
    setFilters({
      ...filters,
      sortField: field,
      sortOrder: newSortOrder
    });
  };

  // Renderizar ícono de ordenamiento
  const renderSortIcon = (field) => {
    if (filters.sortField !== field) {
      return <span className="text-gray-400">⇅</span>;
    }
    return filters.sortOrder === 'asc' ? <span className="text-primary">↑</span> : <span className="text-primary">↓</span>;
  };

  // Cambiar límite de resultados por página
  const handleLimitChange = (newLimit) => {
    setLimit(Number(newLimit));
    setCurrentPage(1); // Resetear a la primera página cuando cambia el límite
  };

  // Efecto para cargar estadísticas y plataformas
  useEffect(() => {
    loadStats();
    loadPlatforms();
  }, [filters]);

  // Formatear fechas para mostrar en la tabla (Europe/Madrid)
  const formatDate = (dateString) => {
    return formatDateToMadridReadable(dateString, 'dd/MM/yyyy HH:mm');
  };

  // Cálculo de rangos mostrados
  const from = total === 0 ? 0 : ((currentPage - 1) * limit) + 1;
  const to = total === 0 ? 0 : Math.min(from + limit - 1, total);

  // Función para abrir el modal de edición
  const handleOpenEditModal = (conversion) => {
    setSelectedConversion(conversion);
    
    const formattedDateForInput = convertUTCToMadridDateTimeLocalString(conversion.conversion_event_time);
    
    console.log('Fecha UTC original:', conversion.conversion_event_time);
    console.log('Fecha formateada para input (Europe/Madrid):', formattedDateForInput);
    
    setEditFormData({
      conversionValue: conversion.conversionValue || '',
      conversion_event_time: formattedDateForInput,
      gclid: conversion.gclid || ''
    });
    
    setShowEditModal(true);
  };

  // Función para cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedConversion(null);
    setEditFormData({
      conversionValue: '',
      conversion_event_time: '',
      gclid: ''
    });
  };

  // Función para manejar cambios en el formulario de edición
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para guardar los cambios de la conversión
  const handleSaveEdit = async () => {
    try {
      if (!selectedConversion) return;
      
      setIsEditing(true);
      
      // Crear el objeto de actualización
      const updateData = {
        conversionValue: parseFloat(editFormData.conversionValue) || 0,
        gclid: editFormData.gclid
      };
      
      // Manejar la fecha de conversión
      if (editFormData.conversion_event_time) {
        const localDateTimeString = editFormData.conversion_event_time;
        console.log('Fecha del formulario (interpretada como Europe/Madrid):', localDateTimeString);
        
        updateData.conversion_event_time = convertMadridDateTimeLocalToUTCString(localDateTimeString);
        
        console.log('Fecha a guardar (UTC):', updateData.conversion_event_time);
      } else {
        updateData.conversion_event_time = null;
      }
      
      // Llamar al servicio para actualizar la conversión
      await updateConversion(selectedConversion.id, updateData);
      
      // Actualizar la lista de conversiones
      await loadConversions();
      
      // Cerrar el modal y mostrar mensaje de éxito
      handleCloseEditModal();
      toast.success('Conversión actualizada correctamente');
    } catch (error) {
      console.error('Error al actualizar la conversión:', error);
      toast.error('Error al actualizar la conversión');
    } finally {
      setIsEditing(false);
    }
  };

  // Función para calcular fechas basadas en el tipo seleccionado
  const calculateDateRange = (type) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';
    
    switch (type) {
      case 'week':
        // Última semana
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        startDate = startDate.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'month':
        // Último mes
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        startDate = startDate.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'custom':
      case 'all':
      default:
        // No hacemos nada, dejamos las fechas vacías
        break;
    }
    
    return { startDate, endDate };
  };

  // Manejar cambios en el tipo de rango de fecha
  const handleDateRangeTypeChange = (e) => {
    const { value } = e.target;
    const dateRange = calculateDateRange(value);
    
    setTempFilters(prev => ({
      ...prev,
      dateRange: {
        type: value,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }
    }));
  };

  // Manejar cambios en los filtros temporales
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('dateRange.')) {
      const [prefix, field] = name.split('.');
      setTempFilters(prev => ({
        ...prev,
        [prefix]: {
          ...prev[prefix],
          [field]: value
        }
      }));
    } else {
      setTempFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Aplicar filtros
  const applyFilters = () => {
    setFilters({
      ...tempFilters,
      sortField: filters.sortField,
      sortOrder: filters.sortOrder
    });
    setCurrentPage(1);
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    const emptyFilters = {
      search: '',
      platform: '',
      saleStatus: '',
      dateRange: {
        type: 'all',
        startDate: '',
        endDate: ''
      },
      filterByZeroHour: false
    };
    
    setTempFilters(emptyFilters);
    setFilters({
      ...emptyFilters,
      sortField: filters.sortField,
      sortOrder: filters.sortOrder
    });
    setCurrentPage(1);
  };

  // Manejar confirmación de eliminación
  const handleDeleteConfirm = async () => {
    try {
      await getConversions(selectedConversion.id);
      setDeleteDialog({ open: false, conversion: null });
      loadConversions();
      toast.success('Conversión eliminada correctamente');
    } catch (err) {
      console.error('Error al eliminar conversión:', err);
      toast.error('Error al eliminar la conversión');
    }
  };

  // Manejar cancelación de eliminación
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, conversion: null });
  };

  const buildExportParams = () => {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.saleStatus) params.append('saleStatus', filters.saleStatus);
    if (filters.dateRange.startDate) params.append('startDate', filters.dateRange.startDate);
    if (filters.dateRange.endDate) params.append('endDate', filters.dateRange.endDate);

    return params;
  };

  // Exportar conversiones
  const exportConversions = () => {
    const params = buildExportParams();

    window.open(`/api/conversions/export/conversions.csv?${params.toString()}`, '_blank');
  };

  const exportComeTripsConversions = () => {
    const params = buildExportParams();

    window.open(`/api/conversions/export/cometrips-conversions.csv?${params.toString()}`, '_blank');
  };

  const exportComeTripsModifiedConversions = () => {
    const params = buildExportParams();

    window.open(`/api/conversions/export/cometrips-modified-conversions.csv?${params.toString()}`, '_blank');
  };

  // Exportar conversiones nuevas
  const exportNewConversions = () => {
    const params = buildExportParams();

    window.open(`/api/conversions/export/new-conversions.csv?${params.toString()}`, '_blank');
  };

  // Exportar conversiones modificadas
  const exportModifiedConversions = () => {
    const params = buildExportParams();

    window.open(`/api/conversions/export/modified-conversions.csv?${params.toString()}`, '_blank');
  };

  // Funciones para manejar selección múltiple
  const handleSelectConversion = (conversionId) => {
    setSelectedConversions(prev => {
      if (prev.includes(conversionId)) {
        return prev.filter(id => id !== conversionId);
      } else {
        return [...prev, conversionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedConversions.length === conversions.length) {
      setSelectedConversions([]);
    } else {
      setSelectedConversions(conversions.map(c => c.id));
    }
  };

  // Función para marcar conversiones para re-exportar
  const handleMarkForReExport = async () => {
    if (selectedConversions.length === 0) {
      toast.error('Selecciona al menos una conversión');
      return;
    }

    console.log('🔍 DEBUG Frontend - Marcando conversiones:');
    console.log('  - selectedConversions:', selectedConversions);
    console.log('  - markDuration:', markDuration);

    try {
      const requestData = {
        conversionIds: selectedConversions,
        durationHours: markDuration
      };

      console.log('  - Enviando datos:', requestData);

      const response = await api.post('/api/conversions/mark-for-reexport', requestData);

      console.log('  - Respuesta recibida:', response.data);

      toast.success(response.data.message);
      setSelectedConversions([]);
      setShowMarkDialog(false);
      loadConversions(); // Recargar para ver los cambios
    } catch (error) {
      console.error('❌ Error en frontend:', error);
      console.error('  - Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Error al marcar conversiones');
    }
  };

  // Función para desmarcar conversiones
  const handleUnmarkForReExport = async () => {
    if (selectedConversions.length === 0) {
      toast.error('Selecciona al menos una conversión');
      return;
    }

    try {
      const response = await api.post('/api/conversions/unmark-for-reexport', {
        conversionIds: selectedConversions
      });

      toast.success(response.data.message);
      setSelectedConversions([]);
      loadConversions(); // Recargar para ver los cambios
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al desmarcar conversiones');
    }
  };

  // Cargar conversiones
  const loadConversions = async () => {
    try {
      setLoading(true);
      
      // Construir parámetros para la API
      const params = {
        page: currentPage,
        limit,
        sortField: filters.sortField,
        sortOrder: filters.sortOrder
      };

      // Añadir filtros adicionales solo si no están vacíos
      if (filters.platform) {
        params.platform = filters.platform;
      }
      
      if (filters.saleStatus) {
        params.saleStatus = filters.saleStatus;
      }

      // Añadir el término de búsqueda
      if (filters.search && filters.search.trim() !== '') {
        params.search = filters.search.trim();
      }

      // Añadir filtro por hora 00:00
      if (filters.filterByZeroHour) {
        params.filterByZeroHour = true;
      }

      // Añadir fechas solo si hay un rango personalizado
      if (filters.dateRange.type === 'custom' && filters.dateRange.startDate && filters.dateRange.endDate) {
        params.startDate = filters.dateRange.startDate;
        params.endDate = filters.dateRange.endDate;
      } else if (filters.dateRange.type !== 'all') {
        const dateRange = calculateDateRange(filters.dateRange.type);
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      
      const data = await getConversions(params);
      
      setConversions(data.conversions || []);
      setSelectedConversions(prevSelected =>
        prevSelected.filter(id => (data.conversions || []).some(conversion => conversion.id === id))
      );
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar conversiones:', error);
      if (error.response) {
        console.error('Datos de respuesta:', error.response.data);
        console.error('Estado:', error.response.status);
      }
      toast.error('Error al cargar las conversiones');
      setLoading(false);
    }
  };

  const saleStatusLabels = {
    pending: 'Pendientes',
    approved: 'Aprobadas',
    rejected: 'Rechazadas'
  };

  const activeDateRange = (() => {
    if (filters.dateRange.type === 'custom') {
      if (filters.dateRange.startDate && filters.dateRange.endDate) {
        return {
          startDate: filters.dateRange.startDate,
          endDate: filters.dateRange.endDate
        };
      }

      return null;
    }

    if (filters.dateRange.type === 'all') {
      return null;
    }

    const calculatedRange = calculateDateRange(filters.dateRange.type);

    if (!calculatedRange.startDate || !calculatedRange.endDate) {
      return null;
    }

    return calculatedRange;
  })();

  const activeDateRangeLabel = activeDateRange
    ? `${activeDateRange.startDate} a ${activeDateRange.endDate}`
    : 'Historico completo';

  const selectedPlatformName = filters.platform
    ? platforms.find((platform) => String(platform.id) === String(filters.platform))?.name || 'Plataforma seleccionada'
    : 'Todas las plataformas';

  const selectedSaleStatus = filters.saleStatus
    ? saleStatusLabels[filters.saleStatus] || filters.saleStatus
    : 'Todos los estados';

  const zeroHourLabel = filters.filterByZeroHour ? 'Solo 00:00 UTC' : 'Todas las horas';
  const conversionsWithValuePercent = Number(stats.withValuePercent || 0);
  const allVisibleSelected = conversions.length > 0 && selectedConversions.length === conversions.length;
  const selectionToggleLabel = allVisibleSelected ? 'Deseleccionar visibles' : 'Seleccionar visibles';

  const summaryCards = [
    {
      label: 'Total conversiones',
      value: stats.total,
      note: `${stats.total} registros dentro del alcance filtrado.`,
      accentClass: 'metric-accent'
    },
    {
      label: 'Valor total',
      value: formatCurrency(stats.totalValue),
      note: 'Suma de comisiones asociadas a las conversiones visibles.',
      accentClass: 'metric-accent'
    },
    {
      label: 'Comision media',
      value: formatCurrency(stats.averageValue),
      note: 'Media calculada solo sobre conversiones con valor distinto de 0 EUR.',
      accentClass: stats.averageValue > 0 ? 'text-success' : 'metric-accent'
    },
    {
      label: 'Conversiones con valor',
      value: stats.withValueCount,
      note: `${conversionsWithValuePercent.toFixed(1)}% del total con comision registrada.`,
      accentClass: stats.withValueCount > 0 ? 'text-warning' : 'metric-accent'
    }
  ];

  return (
    <div className="space-y-8">
      <section className="dashboard-hero">
        <div className="dashboard-hero-grid">
          <div className="space-y-4">
            <p className="page-eyebrow">Seguimiento operativo</p>
            <div className="page-header">
              <h1 className="page-title">Conversiones</h1>
              <p className="page-subtitle">
                Vista alineada con el dashboard para leer el contexto rapido y con el mismo
                patron de tabla que ventas para revisar, corregir y exportar conversiones.
              </p>
            </div>
          </div>

          <div className="context-chip-row">
            <span className="context-chip">
              <strong>Periodo</strong>
              <span>{activeDateRangeLabel}</span>
            </span>
            <span className="context-chip">
              <strong>Plataforma</strong>
              <span>{selectedPlatformName}</span>
            </span>
            <span className="context-chip">
              <strong>Estado</strong>
              <span>{selectedSaleStatus}</span>
            </span>
            <span className="context-chip">
              <strong>Hora UTC</strong>
              <span>{zeroHourLabel}</span>
            </span>
          </div>
        </div>
      </section>

      <div className="hidden" aria-hidden="true">
      {/* Cabecera */}
      <div className="page-header">
        <h1 className="page-title">Estadísticas de Conversiones</h1>
        <div className="page-actions">
          {/* Botones para operaciones con seleccionados */}
          {selectedConversions.length > 0 && (
            <>
              <button
                onClick={() => setShowMarkDialog(true)}
                className="btn btn-info"
                disabled={loading}
              >
                Marcar para Re-exportar ({selectedConversions.length})
              </button>
              <button
                onClick={handleUnmarkForReExport}
                className="btn btn-warning"
                disabled={loading}
              >
                Desmarcar ({selectedConversions.length})
              </button>
            </>
          )}
          
          <button
            onClick={() => exportConversions()}
            className="btn btn-primary"
          >
            Exportar CSV
          </button>
          <button
            onClick={() => exportComeTripsConversions()}
            className="btn btn-outline"
          >
            Exportar ComeTrips
          </button>
          <button
            onClick={() => exportComeTripsModifiedConversions()}
            className="btn btn-outline"
          >
            ComeTrips Mod.
          </button>
          <button
            onClick={() => exportNewConversions()}
            className="btn btn-success"
          >
            Exportar Nuevas
          </button>
          <button
            onClick={() => exportModifiedConversions()}
            className="btn btn-warning"
          >
            Exportar Modificadas
          </button>
        </div>
      </div>

      </div>

      <div className="premium-panel filter-panel">
        <div className="panel-header">
          <p className="panel-kicker">Ajustes de lectura</p>
          <h2 className="panel-title">Filtros de conversiones</h2>
          <p className="panel-subtitle">
            Refina el listado antes de exportar o revisar incidencias horarias.
          </p>
        </div>

        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Buscar</span>
              </label>
              <input
                type="text"
                name="search"
                placeholder="Buscar por ID, Sale ID o GCLID"
                className="input input-bordered w-full"
                value={tempFilters.search}
                onChange={handleTempFilterChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                name="platform"
                className="select select-bordered w-full"
                value={tempFilters.platform}
                onChange={handleTempFilterChange}
              >
                <option value="">Todas las plataformas</option>
                {platforms.map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Estado de venta</span>
              </label>
              <select
                name="saleStatus"
                className="select select-bordered w-full"
                value={tempFilters.saleStatus}
                onChange={handleTempFilterChange}
              >
                <option value="">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobada</option>
                <option value="rejected">Rechazada</option>
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Rango de fechas</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.dateRange.type}
                onChange={handleDateRangeTypeChange}
              >
                <option value="all">Todas las fechas</option>
                <option value="week">Ultimos 7 dias</option>
                <option value="month">Ultimos 30 dias</option>
                <option value="custom">Rango personalizado</option>
              </select>
            </div>

            {tempFilters.dateRange.type === 'custom' && (
              <>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha inicio</span>
                  </label>
                  <input
                    type="date"
                    name="dateRange.startDate"
                    className="input input-bordered w-full"
                    value={tempFilters.dateRange.startDate}
                    onChange={handleTempFilterChange}
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha fin</span>
                  </label>
                  <input
                    type="date"
                    name="dateRange.endDate"
                    className="input input-bordered w-full"
                    value={tempFilters.dateRange.endDate}
                    onChange={handleTempFilterChange}
                  />
                </div>
              </>
            )}

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Filtrar hora 00:00</span>
              </label>
              <button
                className={`btn ${tempFilters.filterByZeroHour ? 'btn-primary' : 'btn-outline'} w-full justify-start`}
                onClick={() => setTempFilters((prev) => ({ ...prev, filterByZeroHour: !prev.filterByZeroHour }))}
                type="button"
              >
                {tempFilters.filterByZeroHour ? 'Solo conversiones 00:00 UTC' : 'Mostrar todas las horas'}
              </button>
            </div>
          </div>

          <div className="flex justify-end mt-4 gap-2">
            <button className="btn btn-ghost" onClick={clearFilters} disabled={loading}>
              Limpiar filtros
            </button>
            <button className="btn btn-primary" onClick={applyFilters} disabled={loading}>
              Aplicar filtros
            </button>
          </div>
        </div>
      </div>

      <div className="hidden" aria-hidden="true">
      {/* Filtros */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Buscar</span>
              </label>
              <input
                type="text"
                placeholder="Buscar por ID"
                className="input input-bordered w-full"
                value={tempFilters.search}
                onChange={(e) => setTempFilters({ ...tempFilters, search: e.target.value })}
              />
            </div>
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.platform}
                onChange={(e) => setTempFilters({ ...tempFilters, platform: e.target.value })}
              >
                <option value="">Todas</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>{platform.name}</option>
                ))}
              </select>
            </div>
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Estado de Venta</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.saleStatus}
                onChange={(e) => setTempFilters({ ...tempFilters, saleStatus: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobado</option>
                <option value="rejected">Rechazado</option>
              </select>
            </div>
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Rango de Fechas</span>
              </label>
              <select
                className="select select-bordered w-full mb-2"
                value={tempFilters.dateRange.type}
                onChange={handleDateRangeTypeChange}
              >
                <option value="all">Todas las fechas</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
                <option value="custom">Rango personalizado</option>
              </select>
              {tempFilters.dateRange.type === 'custom' && (
                <div className="flex gap-2 mt-2">
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={tempFilters.dateRange.startDate}
                    onChange={(e) => setTempFilters({
                      ...tempFilters,
                      dateRange: { ...tempFilters.dateRange, startDate: e.target.value }
                    })}
                  />
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={tempFilters.dateRange.endDate}
                    onChange={(e) => setTempFilters({
                      ...tempFilters,
                      dateRange: { ...tempFilters.dateRange, endDate: e.target.value }
                    })}
                  />
                </div>
              )}
            </div>
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Filtrar Hora 00:00</span>
              </label>
              <button
                className={`btn ${tempFilters.filterByZeroHour ? 'btn-primary' : 'btn-outline'} w-full`}
                onClick={() => setTempFilters(prev => ({ ...prev, filterByZeroHour: !prev.filterByZeroHour }))}
              >
                {tempFilters.filterByZeroHour ? 'Quitar Filtro Hora 00:00' : 'Filtrar Hora 00:00'}
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button className="btn btn-ghost" onClick={clearFilters}>Limpiar Filtros</button>
            <button className="btn btn-primary" onClick={applyFilters}>Aplicar Filtros</button>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      </div>

      <section className="metric-grid">
        {summaryCards.map((card) => (
          <article key={card.label} className="premium-panel metric-card">
            <span className="metric-label">{card.label}</span>
            <div className={`metric-value ${card.accentClass}`}>{card.value}</div>
            <p className="metric-note">{card.note}</p>
          </article>
        ))}
      </section>

      <div className="hidden" aria-hidden="true">
      <div className="stat-grid mb-8">
        <div className="stat-card">
          <h2 className="stat-label">Total Conversiones</h2>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <h2 className="stat-label">Valor Total</h2>
          <div className="stat-value">{formatCurrency(stats.totalValue)}</div>
        </div>
        <div className="stat-card">
          <h2 className="stat-label">Comisión Media (sin 0€)</h2>
          <div className="stat-value">{formatCurrency(stats.averageValue)}</div>
        </div>
        <div className="stat-card">
          <h2 className="stat-label">Comisiones con valor</h2>
          <div className="stat-value">{stats.withValueCount}</div>
          <div className="text-sm text-base-content/70 mt-1">{stats.withValuePercent?.toFixed(1)}% del total</div>
        </div>
      </div>

      </div>

      <section className="space-y-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="panel-header mb-0">
            <p className="panel-kicker">Operativa diaria</p>
            <h2 className="panel-title">Tabla de conversiones</h2>
            <p className="panel-subtitle">
              Ordena, selecciona y exporta con la misma logica visual que el modulo de ventas.
            </p>
          </div>

          <div className="page-actions">
            <button onClick={exportConversions} className="btn btn-primary">
              Exportar CSV
            </button>
            <button onClick={exportComeTripsConversions} className="btn btn-outline">
              Exportar ComeTrips
            </button>
            <button onClick={exportComeTripsModifiedConversions} className="btn btn-outline">
              ComeTrips Mod.
            </button>
            <button onClick={exportNewConversions} className="btn btn-success">
              Exportar nuevas
            </button>
            <button onClick={exportModifiedConversions} className="btn btn-warning">
              Exportar modificadas
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <button
              className="btn btn-sm btn-outline"
              onClick={handleSelectAll}
              disabled={loading || conversions.length === 0}
            >
              {selectionToggleLabel}
              {selectedConversions.length > 0 ? ` (${selectedConversions.length})` : ''}
            </button>

            {selectedConversions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowMarkDialog(true)}
                  className="btn btn-sm btn-info"
                  disabled={loading}
                >
                  Marcar para re-exportar ({selectedConversions.length})
                </button>
                <button
                  onClick={handleUnmarkForReExport}
                  className="btn btn-sm btn-warning"
                  disabled={loading}
                >
                  Desmarcar ({selectedConversions.length})
                </button>
              </div>
            )}
          </div>

          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSortChange('id')} className="cursor-pointer">
                  ID {renderSortIcon('id')}
                </th>
                <th onClick={() => handleSortChange('Sale.id')} className="cursor-pointer">
                  Sale ID {renderSortIcon('Sale.id')}
                </th>
                <th onClick={() => handleSortChange('order_id')} className="cursor-pointer">
                  Order ID {renderSortIcon('order_id')}
                </th>
                <th onClick={() => handleSortChange('gclid')} className="cursor-pointer">
                  GCLID {renderSortIcon('gclid')}
                </th>
                <th onClick={() => handleSortChange('conversionValue')} className="cursor-pointer">
                  Valor {renderSortIcon('conversionValue')}
                </th>
                <th onClick={() => handleSortChange('conversion_event_time')} className="cursor-pointer">
                  Fecha de conversion {renderSortIcon('conversion_event_time')}
                </th>
                <th onClick={() => handleSortChange('createdAt')} className="cursor-pointer">
                  Fecha creacion {renderSortIcon('createdAt')}
                </th>
                <th onClick={() => handleSortChange('updatedAt')} className="cursor-pointer">
                  Fecha actualizacion {renderSortIcon('updatedAt')}
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <span className="loading loading-spinner loading-md"></span>
                  </td>
                </tr>
              ) : conversions.length > 0 ? (
                conversions.map((conversion) => (
                  <tr key={conversion.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedConversions.includes(conversion.id)}
                        onChange={() => handleSelectConversion(conversion.id)}
                      />
                    </td>
                    <td>{conversion.id}</td>
                    <td>
                      <div className="flex flex-col gap-1">
                        <Link to={`/sales/${conversion.Sale.id}`} className="link link-primary">
                          {conversion.Sale.id}
                        </Link>
                        {conversion.Sale?.Platform?.name && (
                          <span
                            className="badge badge-outline"
                            style={{ '--badge-color': conversion.Sale.Platform.color || '#808080' }}
                          >
                            {conversion.Sale.Platform.name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{conversion.order_id || '-'}</td>
                    <td className="font-mono text-xs">{conversion.gclid || '-'}</td>
                    <td>{formatCurrency(conversion.conversionValue)}</td>
                    <td>{conversion.conversion_event_time ? formatDate(conversion.conversion_event_time) : '-'}</td>
                    <td>{conversion.createdAt ? formatDate(conversion.createdAt) : '-'}</td>
                    <td>{conversion.updatedAt ? formatDate(conversion.updatedAt) : '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="table-actions">
                        <button
                          onClick={() => handleOpenEditModal(conversion)}
                          className="table-action-btn table-action-btn-neutral"
                          title="Editar conversion"
                        >
                          <FiEdit size={18} />
                        </button>
                        {isZeroUTCHour(conversion.conversion_event_time) && (
                          <button
                            onClick={() => handleRefreshTime(conversion.id)}
                            disabled={refreshingRowId === conversion.id}
                            className="table-action-btn table-action-btn-info"
                            title="Refrescar hora del evento desde CEM"
                          >
                            {refreshingRowId === conversion.id ? (
                              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <FiRefreshCw size={18} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    No se encontraron conversiones
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-2">
          <div className="flex items-center">
            <span className="mr-2">Mostrar:</span>
            <select
              value={limit}
              onChange={(e) => handleLimitChange(e.target.value)}
              className="select select-bordered select-sm"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          {totalPages > 1 && (
            <div className="pagination join">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="join-item btn btn-sm"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="join-item btn btn-sm"
              >
                ‹
              </button>
              {paginationButtons.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`join-item btn btn-sm ${currentPage === page ? 'btn-active' : ''}`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="join-item btn btn-sm"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="join-item btn btn-sm"
              >
                »
              </button>
            </div>
          )}

          <div className="text-sm">
            Mostrando {from}-{to} de {total} conversiones
          </div>
        </div>
      </section>

      <div className="hidden" aria-hidden="true">
      {/* Tabla de conversiones */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Conversiones</h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedConversions.length === conversions.length && conversions.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th onClick={() => handleSortChange('id')} className="cursor-pointer">ID {renderSortIcon('id')}</th>
                  <th onClick={() => handleSortChange('Sale.id')} className="cursor-pointer">Sale ID {renderSortIcon('Sale.id')}</th>
                  <th onClick={() => handleSortChange('order_id')} className="cursor-pointer">Order ID {renderSortIcon('order_id')}</th>
                  <th onClick={() => handleSortChange('gclid')} className="cursor-pointer">GCLID {renderSortIcon('gclid')}</th>
                  <th onClick={() => handleSortChange('conversionValue')} className="cursor-pointer">Valor {renderSortIcon('conversionValue')}</th>
                  <th onClick={() => handleSortChange('conversion_event_time')} className="cursor-pointer">Fecha de conversión {renderSortIcon('conversion_event_time')}</th>
                  <th onClick={() => handleSortChange('createdAt')} className="cursor-pointer">Fecha Creación {renderSortIcon('createdAt')}</th>
                  <th onClick={() => handleSortChange('updatedAt')} className="cursor-pointer">Fecha Actualización {renderSortIcon('updatedAt')}</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      <span className="loading loading-spinner loading-md"></span>
                    </td>
                  </tr>
                ) : conversions.length > 0 ? (
                  conversions.map((conversion) => (
                    <tr key={conversion.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedConversions.includes(conversion.id)}
                          onChange={() => handleSelectConversion(conversion.id)}
                        />
                      </td>
                      <td>{conversion.id}</td>
                      <td>
                        <Link to={`/sales/${conversion.Sale.id}`} className="link link-primary">
                          {conversion.Sale.id}
                        </Link>
                      </td>
                      <td>{conversion.order_id}</td>
                      <td>{conversion.gclid}</td>
                      <td>{formatCurrency(conversion.conversionValue)}</td>
                      <td>{formatDate(conversion.conversion_event_time)}</td>
                      <td>{formatDate(conversion.createdAt)}</td>
                      <td>{formatDate(conversion.updatedAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="table-actions">
                          <button 
                          onClick={() => handleOpenEditModal(conversion)}
                          className="table-action-btn table-action-btn-neutral"
                          title="Editar Conversión"
                        >
                          <FiEdit size={18} />
                        </button>
                        {/* Botón para refrescar la hora del evento */}
                        {isZeroUTCHour(conversion.conversion_event_time) && (
                          <button
                            onClick={() => handleRefreshTime(conversion.id)}
                            disabled={refreshingRowId === conversion.id}
                            className="table-action-btn table-action-btn-info"
                            title="Refrescar hora del evento desde CEM"
                          >
                            {refreshingRowId === conversion.id ? (
                              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <FiRefreshCw size={18} />
                            )}
                          </button>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No se encontraron conversiones
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Paginación */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-2">
            <div className="flex items-center">
              <span className="mr-2">Mostrar:</span>
              <select
                value={limit}
                onChange={(e) => handleLimitChange(e.target.value)}
                className="select select-bordered select-sm"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="pagination join">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="join-item btn btn-sm"
                >
                  «
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="join-item btn btn-sm"
                >
                  ‹
                </button>
                {paginationButtons.map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`join-item btn btn-sm ${currentPage === page ? 'btn-active' : ''}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="join-item btn btn-sm"
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="join-item btn btn-sm"
                >
                  »
                </button>
              </div>
            )}
            <div className="text-sm">
              Mostrando {from}-{to} de {total} conversiones
            </div>
          </div>
        </div>
      </div>

      {/* Modal de edición de conversión */}
      </div>

      {showEditModal && selectedConversion && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              Editar Conversión #{selectedConversion.id}
            </h3>
            
            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text font-medium">Valor de Conversión</span>
              </label>
              <input 
                type="number" 
                name="conversionValue" 
                className="input input-bordered w-full" 
                value={editFormData.conversionValue}
                onChange={handleEditFormChange}
                step="0.01"
              />
            </div>
            
            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text font-medium">Fecha y Hora de Conversión</span>
              </label>
              <input 
                type="datetime-local" 
                name="conversion_event_time" 
                className="input input-bordered w-full" 
                value={editFormData.conversion_event_time}
                onChange={handleEditFormChange}
              />
            </div>
            
            <div className="form-control w-full mb-4">
              <label className="label">
                <span className="label-text font-medium">GCLID</span>
              </label>
              <input 
                type="text" 
                name="gclid" 
                className="input input-bordered w-full" 
                value={editFormData.gclid}
                onChange={handleEditFormChange}
              />
            </div>
            
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={handleCloseEditModal} disabled={isEditing}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={isEditing}>
                {isEditing ? (
                  <>
                    <span className="loading loading-spinner loading-xs mr-2"></span>
                    Guardando...
                  </>
                ) : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {deleteDialog.open && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmar Eliminación</h3>
            <p className="py-4">
              ¿Estás seguro de que deseas eliminar esta conversión? Esta acción no se puede deshacer.
            </p>
            <div className="modal-action">
              <button onClick={handleDeleteCancel} className="btn">
                Cancelar
              </button>
              <button onClick={handleDeleteConfirm} className="btn btn-error">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para marcar conversiones para re-exportar */}
      {showMarkDialog && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Marcar Conversiones para Re-exportar</h3>
            <p className="py-4">
              Las conversiones seleccionadas aparecerán en el próximo export de "Conversiones Nuevas" durante el tiempo especificado.
            </p>
            
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Duración (horas)</span>
              </label>
              <input
                type="number"
                min="1"
                max="168"
                className="input input-bordered w-full"
                value={markDuration}
                onChange={(e) => setMarkDuration(parseInt(e.target.value) || 48)}
                placeholder="48"
              />
              <label className="label">
                <span className="label-text-alt">Máximo 168 horas (7 días)</span>
              </label>
            </div>

            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => setShowMarkDialog(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleMarkForReExport}
              >
                Marcar Conversiones
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionsScreen; 
